import { memo, type ReactNode, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import classnames from "classnames";

import Env from "config/environment";
import { userFullName } from "util/user";
import { useLoadScript } from "util/html";
import supportImg from "assets/images/support.svg";
import { CURRENT_PORTAL } from "constants/app_subdomains";
import ActionButton from "common/core/action_button";
import { segmentTrack } from "util/segment";
import { SEGMENT_EVENTS } from "constants/analytics";
import { useColorConstant } from "constants/color";

import type { ViewerChatProperties_viewer as Viewer } from "./chat_query.graphql";
import Styles from "./zendesk.module.scss";

type Props = {
  viewer: Viewer | null;
  onToggle?: (open: boolean) => void;
  showSupportButton: boolean;
};
type ButtonProps = {
  className?: string;
  children?: ReactNode;
};
type PrefillArgs = {
  name: {
    value: string;
    readOnly?: boolean;
  };
  email: {
    value: string | null;
    readOnly?: boolean;
  };
};

declare global {
  interface Window {
    zESettings: {
      webWidget: {
        position?: { horizontal: "left" | "right"; vertical: "top" | "bottom" };
        color?: { theme: string };
        contactForm?: {
          attachments: boolean;
        };
      };
    };
    zE?: {
      (widget: "webWidget", operation: "hide" | "open" | "show"): void;
      (widget: "webWidget", operation: "setLocale", locale: string): void;
      (widget: "webWidget", operation: "prefill", data: PrefillArgs): void;
      (widget: "webWidget", operation: "chat:addTags", tags: string[]): void;
      (widget: "webWidget:on", operation: "open" | "close", callback: () => void): void;
    };
  }
}

const { zendeskChatApiKey } = Env;
const MESSAGES = defineMessages({
  helpButtonLabel: {
    id: "7e4bd519-79fa-42d5-89af-577b5205db2a",
    defaultMessage: "Notarize Help",
  },
});

function openZendesk() {
  window.zE?.("webWidget", "show");
  window.zE?.("webWidget", "open");
  segmentTrack(SEGMENT_EVENTS.OPENED_SUPPORT_CHAT);
}

function ZendeskButton({ className, children }: ButtonProps) {
  return (
    <ActionButton
      color="dark"
      className={className ? className : Styles.fullButton}
      onClick={openZendesk}
      automationId="support-button"
    >
      <img alt="" src={supportImg} />
      {children || (
        <FormattedMessage id="f1238942-e489-4f69-b141-acf558134018" defaultMessage="Support" />
      )}
    </ActionButton>
  );
}

const MemoizedZendeskButton = memo(ZendeskButton);
export { MemoizedZendeskButton as ZendeskButton };

function FixedZendeskButton({ open, intl }: { open: boolean; intl: ReturnType<typeof useIntl> }) {
  return (
    <button
      type="button"
      onClick={openZendesk}
      className={classnames(Styles.fixedButton, open && Styles.open)}
      aria-label={intl.formatMessage(MESSAGES.helpButtonLabel)}
      aria-haspopup="true"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M0 0h24v24H0z" fill="none" />
        <path
          d="M11.5 2C6.81 2 3 5.81 3 10.5S6.81 19 11.5 19h.5v3c4.86-2.34 8-7 8-11.5C20 5.81 16.19 2 11.5 2zm1 14.5h-2v-2h2v2zm0-3.5h-2c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2h-2c0-2.21 1.79-4 4-4s4 1.79 4 4c0 2.5-3 2.75-3 5z"
          fill="white"
        />
      </svg>
    </button>
  );
}

function setupZendesk(
  user: Viewer["user"],
  locale: string,
  themeColor: string,
  onToggle: (newVal: boolean) => void,
) {
  window.zESettings = {
    webWidget: {
      color: { theme: themeColor },
      position: { horizontal: "right", vertical: "bottom" },
      contactForm: {
        attachments: false,
      },
    },
  };
  const zendesk = window.zE!;
  let webWidgetTimer: ReturnType<typeof setInterval> | undefined;
  zendesk("webWidget", "hide");
  zendesk("webWidget:on", "close", () => {
    window.zE?.("webWidget", "hide");
    onToggle(false);
    clearInterval(webWidgetTimer);
  });
  zendesk("webWidget:on", "open", () => {
    onToggle(true);
    // Adding a timer here for accessibility purposes, to focus on the zendesk window on open.
    webWidgetTimer = setInterval(() => {
      const webWidget = document.querySelector("iframe#webWidget");
      if (webWidget) {
        (webWidget as HTMLIFrameElement).contentWindow?.focus();
        clearInterval(webWidgetTimer);
      }
    }, 250);
  });
  if (locale) {
    zendesk("webWidget", "setLocale", locale);
  }
  if (user) {
    zendesk("webWidget", "chat:addTags", [user.id, CURRENT_PORTAL]);
    zendesk("webWidget", "prefill", {
      name: { value: userFullName(user) },
      email: { value: user.email, readOnly: true },
    });
  }
}

function ZendeskChat({ viewer, onToggle, showSupportButton }: Props) {
  const COLOR = useColorConstant();
  const user = viewer?.user;
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const onToggleRef = useRef(onToggle);
  useEffect(() => {
    onToggleRef.current = onToggle;
  });
  useLoadScript({
    id: "ze-snippet",
    src: `https://static.zdassets.com/ekr/snippet.js?key=${zendeskChatApiKey}`,
    onLoad: () =>
      setupZendesk(user!, intl.locale, COLOR.BLUE, (newVal) => {
        setOpen(newVal);
        onToggleRef.current?.(newVal);
      }),
  });

  return showSupportButton ? <FixedZendeskButton open={open} intl={intl} /> : null;
}

export default memo(ZendeskChat);
